import { For, Show, type JSX } from "solid-js";
import { formatUSD } from "~/util/format";
import { DASH } from "~/util/html/codepoint";
import { clsx } from "~/util/html/style";

// two offices can share a name, never an id: blocks group by the id and print the name.
type Office = { id: string; name: string };

type Row = {
  office: Office | null;
  item: string;
  quantity: number | null;
  priceEachCents: number | null;
  amountCents: number;
  // what Twelve covers of the row's units; null when it covers none.
  cover?: {
    total: number;
    covered: number;
    billed: number;
    coverage: "included" | "covered";
  } | null;
};

type Tax = {
  office: Office | null;
  item: string;
  amountCents: number;
  part: boolean;
};

// a one-time charge twelve covered: shown under its office, never in a total.
type Absorbed = {
  office: Office | null;
  item: string;
  amountCents: number;
};

export type PhoneBreakdownTableProps = {
  rows: Row[];
  serviceCents: number;
  taxes: Tax[];
  absorbed: Absorbed[];
  totalCents: number;
  serviceLabel?: string;
  totalLabel?: string;
  showTotal?: boolean;
};

const ALL_OFFICES = "All offices";
const ABSORBED_NOTE = "not billed, covered by Twelve";
const COVERAGE_WORDS = {
  included: "included by Twelve",
  covered: "covered by Twelve",
} as const;

function money(cents: number): string {
  return formatUSD(cents, { fromCents: true });
}

// "10 total · 4 included by Twelve · 6 billed": the amount stays the file's, this says who pays for it.
function coverageNote(row: Row): string | null {
  const cover = row.cover;
  if (cover == null) return null;
  return `${cover.total} total · ${cover.covered} ${COVERAGE_WORDS[cover.coverage]} · ${cover.billed} billed`;
}

type OfficeBlock = { name: string; rows: Row[]; taxes: Tax[]; absorbed: Absorbed[] };

// one block per office, in the order the rows arrive; a row with no office belongs to every office. a tax
// with no office is the account's and stays under the subtotal; one naming its office joins that block.
function officeBlocks(rows: Row[], taxes: Tax[], absorbed: Absorbed[]): OfficeBlock[] {
  const blocks = new Map<string, OfficeBlock>();
  const blockOf = (office: Office | null): OfficeBlock => {
    const key = office?.id ?? ALL_OFFICES;
    const block = blocks.get(key) ?? {
      name: office?.name ?? ALL_OFFICES,
      rows: [],
      taxes: [],
      absorbed: [],
    };
    blocks.set(key, block);
    return block;
  };
  for (const row of rows) blockOf(row.office).rows.push(row);
  for (const tax of taxes) if (tax.office !== null) blockOf(tax.office).taxes.push(tax);
  for (const charge of absorbed) blockOf(charge.office).absorbed.push(charge);
  return [...blocks.values()];
}

function Cell(props: {
  right?: boolean;
  indent?: boolean;
  // a part of the row above it, one step further in.
  nested?: boolean;
  class?: string;
  children?: JSX.Element;
}) {
  return (
    <td
      class={clsx(
        "py-2 align-top",
        props.right ? "pl-6 text-right tabular-nums" : "pr-6",
        props.nested ? "pl-10" : props.indent && "pl-5",
        props.class,
      )}
    >
      {props.children}
    </td>
  );
}

export function PhoneBreakdownTable(props: PhoneBreakdownTableProps) {
  const accountTaxes = () => props.taxes.filter((tax) => tax.office === null);
  const whole = () => accountTaxes().filter((tax) => !tax.part);
  const parts = () => accountTaxes().filter((tax) => tax.part);
  return (
    <div class="overflow-x-auto py-6 print:overflow-visible">
      <table class="w-full min-w-[32rem] border-collapse">
        <thead>
          <tr class="border-b border-brand-subtle text-left text-typography-secondary print:border-black print:text-black">
            <th class="pr-6 pb-2 font-medium">Item</th>
            <th class="pb-2 pl-6 text-right font-medium">Quantity</th>
            <th class="pb-2 pl-6 text-right font-medium">Price each</th>
            <th class="pb-2 pl-6 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <For each={officeBlocks(props.rows, props.taxes, props.absorbed)}>
          {(block) => (
            <tbody>
              <tr>
                <th scope="rowgroup" colSpan={4} class="pt-4 pb-2 text-left font-semibold">
                  {block.name}
                </th>
              </tr>
              <For each={block.rows}>
                {(row) => (
                  <tr
                    class={clsx(
                      "border-b border-brand-subtle/60 print:border-black/30",
                      row.amountCents === 0 && "text-typography-secondary print:text-black",
                    )}
                  >
                    <Cell indent>
                      {row.item}
                      <Show when={coverageNote(row)}>
                        {(note) => (
                          <div class="text-typography-secondary print:text-black">{note()}</div>
                        )}
                      </Show>
                    </Cell>
                    <Cell right>{row.quantity ?? DASH}</Cell>
                    <Cell right>
                      {row.priceEachCents === null ? DASH : money(row.priceEachCents)}
                    </Cell>
                    <Cell right>{money(row.amountCents)}</Cell>
                  </tr>
                )}
              </For>
              <For each={block.taxes}>
                {(tax) => (
                  <tr
                    class={
                      tax.part
                        ? "text-typography-secondary print:text-black"
                        : "border-b border-brand-subtle/60 print:border-black/30"
                    }
                  >
                    <Cell indent nested={tax.part}>
                      {tax.item}
                    </Cell>
                    <Cell />
                    <Cell />
                    <Cell right>{money(tax.amountCents)}</Cell>
                  </tr>
                )}
              </For>
              <For each={block.absorbed}>
                {(charge) => (
                  <tr class="border-b border-brand-subtle/60 text-typography-secondary print:border-black/30 print:text-black">
                    <Cell indent>
                      {[charge.item, money(charge.amountCents), ABSORBED_NOTE].join(", ")}
                    </Cell>
                    <Cell />
                    <Cell />
                    <Cell />
                  </tr>
                )}
              </For>
            </tbody>
          )}
        </For>
        <tbody>
          <tr class="border-t border-typography-primary print:border-black">
            <Cell class="font-semibold">{props.serviceLabel ?? "Phone service"}</Cell>
            <Cell />
            <Cell />
            <Cell right class="font-semibold">
              {money(props.serviceCents)}
            </Cell>
          </tr>
          <For each={whole()}>
            {(tax) => (
              <tr>
                <Cell>{tax.item}</Cell>
                <Cell />
                <Cell />
                <Cell right>{money(tax.amountCents)}</Cell>
              </tr>
            )}
          </For>
          <For each={parts()}>
            {(tax) => (
              <tr class="text-typography-secondary print:text-black">
                <Cell indent>{tax.item}</Cell>
                <Cell />
                <Cell />
                <Cell right>{money(tax.amountCents)}</Cell>
              </tr>
            )}
          </For>
          <Show when={props.showTotal || props.taxes.length > 0}>
            <tr class="border-t-2 border-typography-primary text-base print:border-black">
              <Cell class="font-semibold">{props.totalLabel ?? "Total"}</Cell>
              <Cell />
              <Cell />
              <Cell right class="font-semibold">
                {money(props.totalCents)}
              </Cell>
            </tr>
          </Show>
        </tbody>
      </table>
    </div>
  );
}
